import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import api from '../../services/api';
import { 
  offerStandaloneService, 
  type CreateOfferStandaloneRequest 
} from '../../services/offerStandaloneService';
import { useNotification } from '../layouts/Layout';

interface AddOfferAIFormProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (formData: CreateOfferStandaloneRequest) => void;
}

const AddOfferAIForm: React.FC<AddOfferAIFormProps> = ({
  open,
  onClose,
  onGenerated
}) => {
  const [generating, setGenerating] = useState(false);
  const [swzText, setSwzText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { showNotification } = useNotification();

  useEffect(() => {
    if (!open) {
      setError('');
      setSwzText('');
      try { (window as any).__unitrescoAddOfferAIOpen = false; } catch {}
    } else {
      try { (window as any).__unitrescoAddOfferAIOpen = true; } catch {}
    }
    return () => {
      try { (window as any).__unitrescoAddOfferAIOpen = false; } catch {}
    };
  }, [open]);

  async function handleGenerate() {
    if (!swzText.trim()) {
      setError('Wklej tekst SWZ.');
      return;
    }

    // Sprawdź czy użytkownik jest zalogowany
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Brak autoryzacji. Zaloguj się ponownie.');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: true } })); } catch {}
      
      // Tworzymy tymczasowy plik tekstowy z zawartością SWZ
      const blob = new Blob([swzText], { type: 'text/plain' });
      const file = new File([blob], 'swz.txt', { type: 'text/plain' });
      
      const form = new FormData();
      form.append('files', file);
      try {
        const saved = localStorage.getItem('ai:instr:prequal');
        if (saved && saved.trim()) {
          form.append('prompt', saved);
        }
      } catch {}
      
      const headers: Record<string,string> = { 'x-ai-provider': 'deepseek', 'x-ai-model': 'deepseek-chat' };
      const apiKey = localStorage.getItem('ai:apiKey:deepseek') || '';
      if (apiKey) headers['x-ai-api-key'] = apiKey;
      
      const resp = await api.post('/ai/offers/extract-json', form, { headers });
      const arr = resp.data?.data?.items || [];
      if (arr.length > 0) {
        const result = await offerStandaloneService.validateImport(arr);
        if (result.report && result.report.length > 0 && result.report[0].ok) {
          const normalized = result.report[0].normalized as any;
          const generatedData: CreateOfferStandaloneRequest = {
            ...normalized,
            status: 'Prekwalifikacja',
            site_inspection: (normalized?.site_inspection as any) || 'Nie',
            offer_preparer: '__AI__'
          };
          showNotification('Dane oferty zostały wygenerowane przez AI', 'success');
          onClose();
          onGenerated(generatedData);
        } else {
          setError('Nie udało się wygenerować poprawnych danych oferty.');
        }
      } else {
        setError('Nie znaleziono danych oferty w tekście SWZ.');
      }
    } catch (e: any) {
      console.error('AI extract error', e);
      if (e.response?.status === 401) {
        setError('Błąd autoryzacji. Zaloguj się ponownie.');
      } else {
        setError(e.response?.data?.message || 'Nie udało się wygenerować danych z tekstu SWZ.');
      }
    } finally {
      setGenerating(false);
      try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: false } })); } catch {}
    }
  }


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Dodaj ofertę przez AI
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 4, py: 3, flex: 1, overflow: 'auto' }}>
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              
              {swzText.trim() ? (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label="Wklejono SWZ..."
                    onDelete={() => setSwzText('')}
                    sx={{
                      bgcolor: '#FEEED6',
                      color: '#020242',
                      borderRadius: '4px',
                      minHeight: 32,
                      height: 32,
                      '& .MuiChip-label': {
                        px: 1.5,
                        py: 0,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: '#020242'
                      },
                      '& .MuiChip-deleteIcon': {
                        color: '#020242',
                        fontSize: '18px'
                      }
                    }}
                  />
                </Box>
                ) : (
                  <TextField
                    label="Wklej SWZ"
                  multiline
                  rows={10}
                  value={swzText}
                  onChange={(e) => setSwzText(e.target.value)}
                  onPaste={(e) => {
                    const pastedText = e.clipboardData.getData('text');
                    if (pastedText) {
                      setSwzText(pastedText);
                      e.preventDefault();
                    }
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Wklej tutaj tekst specyfikacji warunków zamówienia..."
                />
              )}
              
              {generating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: '#6b46c1', color: '#fff', borderRadius: 1 }}>
                  <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 16, height: 16 }} />
                  <Typography variant="body2">AI generuje dane oferty...</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ borderTop: '1px solid #EDEDED' }} />
          <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
            <Box />
            <Box>
              <Button 
                onClick={onClose} 
                variant="text"
                disabled={generating}
                sx={{ 
                  fontFamily: 'Roboto, sans-serif', 
                  fontWeight: 500, 
                  fontSize: 14, 
                  color: '#2D2D32', 
                  textTransform: 'none' 
                }}
              >
                Anuluj
              </Button>
              <Button
                onClick={handleGenerate}
                variant="contained"
                disabled={generating || !swzText.trim()}
                sx={{ 
                  backgroundColor: '#020242',
                  '&:hover': {
                    backgroundColor: '#030360'
                  }
                }}
              >
                {generating ? (
                  <>
                    <CircularProgress size={24} color="inherit" />
                  </>
                ) : (
                  'Generuj'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddOfferAIForm;

